import { Component, OnInit, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from '../sidebar/sidebar';
import { ChatAreaComponent } from '../chat-area/chat-area';
import { RoleService } from '../../../../shared/services/role.service';
import { ChatHistoryMessage } from '../../interfaces/chat-history-response.interface';
import { Subscription } from 'rxjs';
import { OrganogramIdentityService } from '../../services/organogram-identity.service';
import { LeaveService } from '../../services/leave.service';

@Component({
  selector: 'app-chat-layout',
  standalone: true,
  imports: [CommonModule, SidebarComponent, ChatAreaComponent],
  templateUrl: './chat-layout.html'
})
export class ChatLayoutComponent implements OnInit {
  @ViewChild(ChatAreaComponent) chatAreaComponent!: ChatAreaComponent;
  @ViewChild(SidebarComponent) sidebarComponent!: SidebarComponent;

  isSidebarCollapsed = false;
  isMobileMenuOpen = false;
  showCollections = false;
  showFeedbackAdmin = false;
  showMailbox = false;
  showVisualizations = false;
  showInferences = false;
  showClusters = false;
  showLeave = false;
  showAgentHooks = false;
  /** Only an admin of the workspace sees the Agent hooks page. */
  canViewAgentHooks = false;
  canViewCollections = false;
  canViewFeedback = false;
  canViewMailbox = false;
  canViewVisualizations = false;
  canViewInferences = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private roleService: RoleService,
    private organogramIdentity: OrganogramIdentityService,
    private leaveService: LeaveService
  ) {}

  ngOnInit(): void {
    this.canViewCollections = this.roleService.canViewCollections();
    this.canViewFeedback = this.roleService.canViewFeedback();
    this.canViewMailbox = this.roleService.canViewMailbox();
    this.canViewVisualizations = this.roleService.canViewVisualizations();
    this.canViewInferences = this.roleService.canViewInferences();
    this.organogramIdentity.refresh();
    this.leaveService.getMySystemRole().subscribe({
      next: (res) => (this.canViewAgentHooks = res.data?.systemRole === 'Admin'),
      error: () => (this.canViewAgentHooks = false)
    });
    if (window.innerWidth <= 768) {
      this.isSidebarCollapsed = false;
    }
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  private clearFeaturePanelsExcept(keep?: 'collections' | 'feedback' | 'mailbox' | 'visualizations' | 'inferences' | 'clusters' | 'leave' | 'agentHooks'): void {
    this.showCollections = keep === 'collections';
    this.showFeedbackAdmin = keep === 'feedback';
    this.showMailbox = keep === 'mailbox';
    this.showVisualizations = keep === 'visualizations';
    this.showInferences = keep === 'inferences';
    this.showClusters = keep === 'clusters';
    this.showLeave = keep === 'leave';
    this.showAgentHooks = keep === 'agentHooks';
  }

  onCollectionsClicked(): void {
    if (this.canViewCollections) {
      this.clearFeaturePanelsExcept('collections');
    }
  }

  onFeedbackClicked(): void {
    if (this.canViewFeedback) {
      this.clearFeaturePanelsExcept('feedback');
    }
  }

  onMailboxClicked(): void {
    if (this.canViewMailbox) {
      this.clearFeaturePanelsExcept('mailbox');
    }
  }

  onVisualizationsClicked(): void {
    if (this.canViewVisualizations) {
      this.clearFeaturePanelsExcept('visualizations');
    }
  }

  onInferencesClicked(): void {
    if (this.canViewInferences) {
      this.clearFeaturePanelsExcept('inferences');
    }
  }

  onClustersClicked(): void {
    if (this.canViewInferences) {
      this.clearFeaturePanelsExcept('clusters');
    }
  }

  onLeaveClicked(): void {
    this.clearFeaturePanelsExcept('leave');
  }

  onAgentHooksClicked(): void {
    if (this.canViewAgentHooks) {
      this.clearFeaturePanelsExcept('agentHooks');
    }
  }

  onPromptsClicked(): void {
    if (this.chatAreaComponent) {
      this.chatAreaComponent.openPromptsGalleryModal();
    }
  }

  onBackToChat(): void {
    this.clearFeaturePanelsExcept();
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  @HostListener('window:resize', ['$event'])
  onResize(_event: any) {
    if (window.innerWidth <= 768 && this.isSidebarCollapsed) {
      this.isSidebarCollapsed = false;
    }
  }

  onNewChatClicked(): void {
    if (this.chatAreaComponent) {
      this.chatAreaComponent.startNewChat();
    }
    this.clearFeaturePanelsExcept();
  }

  onChatSaved(chatData: { chatId: string; label: string }): void {
    if (this.sidebarComponent) {
      this.sidebarComponent.addSavedChat(chatData.chatId, chatData.label);
    }
  }

  onChatHistoryItemClicked(data: { messages: ChatHistoryMessage[]; chatId: string; chatLabel?: string; isLoading?: boolean }): void {
    this.clearFeaturePanelsExcept();

    if (this.chatAreaComponent && data.messages.length > 0) {
      this.chatAreaComponent.loadChatFromHistory(data.messages, data.chatId, data.isLoading, data.chatLabel);
    } else if (this.chatAreaComponent && data.isLoading) {
      this.chatAreaComponent.loadChatFromHistory(data.messages, data.chatId, data.isLoading, data.chatLabel);
    }
  }

  onChatDeleted(_chatId: string): void {
    if (this.chatAreaComponent) {
      this.chatAreaComponent.startNewChat();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
